import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ROISection from '../components/lead-analytics/ROISection';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Mail, Users, TrendingUp, AlertCircle, Loader2, Globe,
  RefreshCw, Database, Clock, CheckCircle2, Play, Link2Off,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#002FA7', '#2A9D8F', '#E76F51', '#E9C46A', '#264653', '#F4A261', '#606c38', '#bc6c25', '#5e60ce', '#d62828'];
const SOURCE_COLORS = { 'Google Organic': '#34A853', 'Google Ads': '#FBBC04', 'Facebook': '#1877F2', 'LinkedIn': '#0A66C2', 'Instagram': '#E4405F', 'Referral': '#8B5CF6', 'Formulaire site web': '#2A9D8F', 'Email direct': '#94A3B8', 'WordPress': '#21759B', 'Wix': '#0C6EFC' };
const xTick = { fontSize: 11, fill: '#999' };
const yTick = { fontSize: 11, fill: '#999' };
const COMPANY_DOT = { r: 5, fill: '#002FA7' };
const LINE_DOT = { r: 4 };

export default function LeadAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [computingMonth, setComputingMonth] = useState('');
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugMonth, setDebugMonth] = useState('');
  const [error, setError] = useState('');
  const [months, setMonths] = useState(6);
  const [view, setView] = useState('client');
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedConsultant, setSelectedConsultant] = useState('all');
  const [triggeringScheduler, setTriggeringScheduler] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);

  const fetchGmailStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
      setGmailStatus(data);
    } catch (e) {
      console.warn('Gmail status check failed (non-blocking):', e?.response?.status || e?.message);
    }
  };

  const fetchClients = async () => {
    try {
      const { data: cs } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClientsList(cs);
    } catch (e) {
      console.warn('Could not load clients', e);
    }
  };

  const toggleClientInStats = async (clientId, include) => {
    try {
      await axios.put(
        `${API}/clients/${clientId}/stats-settings`,
        { include_in_stats: include },
        { withCredentials: true }
      );
      toast.success(include ? 'Client inclus dans les stats' : 'Client exclu des stats');
      fetchClients();
      fetchData(months);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const fetchData = async (m) => {
    setLoading(true);
    setError('');
    try {
      const { data: d } = await axios.get(`${API}/settings/leads/stats?months=${m}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors du chargement';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(months);
    fetchClients();
    fetchGmailStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthsChange = (m) => {
    setMonths(m);
    fetchData(m);
  };

  const computeAll = async () => {
    setComputing(true);
    try {
      const { data: result } = await axios.post(`${API}/settings/leads/compute-all?months=${months}`, {}, { withCredentials: true });
      toast.success(result.message);
      await fetchData(months);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur lors du calcul';
      if (typeof detail === 'string' && detail.toLowerCase().includes('invalid_grant')) {
        toast.error("Gmail déconnecté. Cliquez sur 'Reconnecter Gmail' dans la page Leads.", { duration: 8000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setComputing(false);
    }
  };

  const recomputeAll = async () => {
    if (!window.confirm('Recalculer tous les mois avec les mots-cles actuels ? Cela prend 1-2 minutes en arriere-plan.')) return;
    setRecomputing(true);
    try {
      const { data: result } = await axios.post(`${API}/settings/leads/recompute-all?months=${months}`, {}, { withCredentials: true });
      toast.success(result.message);
      // Auto-refresh after 90 seconds to pick up background results
      setTimeout(() => { fetchData(months); setRecomputing(false); }, 90000);
      setTimeout(() => fetchData(months), 30000); // intermediate refresh
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du recalcul');
      setRecomputing(false);
    }
  };

  const computeMonth = async (ym) => {
    setComputingMonth(ym);
    try {
      const { data: result } = await axios.post(`${API}/settings/leads/compute/${ym}`, {}, { withCredentials: true });
      toast.success(result.message);
      // Poll until the month doc reappears in lead_stats (background compute)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data: d } = await axios.get(`${API}/settings/leads/stats?months=${months}`, { withCredentials: true });
          const done = d.computed_months?.includes(ym);
          if (done || attempts >= 24) {
            clearInterval(poll);
            setComputingMonth('');
            setData(d);
            if (done) {
              toast.success(`Mois ${ym} calculé ✔`);
            } else {
              toast.error(`Le calcul de ${ym} prend plus de temps que prévu — cliquez Rafraîchir dans une minute`);
            }
          }
        } catch { /* keep polling */ }
      }, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du calcul');
      setComputingMonth('');
    }
  };

  const triggerScheduler = async () => {
    setTriggeringScheduler(true);
    try {
      const { data: result } = await axios.post(`${API}/settings/scheduler/run-now`, {}, { withCredentials: true });
      toast.success(result.message);
      setTimeout(() => fetchData(months), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setTriggeringScheduler(false);
    }
  };

  const loadUnmatchedEmails = async (month) => {
    setDebugLoading(true);
    setDebugMonth(month);
    try {
      const { data: d } = await axios.get(`${API}/settings/leads/debug-matching?month=${month}`, { withCredentials: true });
      setDebugData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setDebugLoading(false);
    }
  };

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo) - 1]} ${y.slice(2)}`;
  };

  const getSourceColor = (source) => SOURCE_COLORS[source] || COLORS[Object.keys(SOURCE_COLORS).length % COLORS.length];

  const companyChartData = useMemo(() => {
    if (!data?.month_labels || !data?.company_monthly) return [];
    return data.company_monthly.map(item => ({
      month: formatMonth(item.month),
      'Total Leads': item.count,
    }));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientChartData = useMemo(() => {
    if (!data?.month_labels) return [];
    const filtered = selectedClient === 'all'
      ? data.by_client.filter(c => c.total > 0)
      : data.by_client.filter(c => c.client_id === selectedClient);
    return data.month_labels.map((m, idx) => {
      const point = { month: formatMonth(m) };
      filtered.forEach(c => { point[c.client_name] = c.monthly[idx]?.count || 0; });
      return point;
    });
  }, [data, selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const consultantChartData = useMemo(() => {
    if (!data?.month_labels) return [];
    const filtered = selectedConsultant === 'all'
      ? data.by_consultant
      : data.by_consultant.filter(c => c.consultant_id === selectedConsultant);
    return data.month_labels.map((m, idx) => {
      const point = { month: formatMonth(m) };
      filtered.forEach(c => { point[c.consultant_name] = c.monthly[idx]?.count || 0; });
      return point;
    });
  }, [data, selectedConsultant]); // eslint-disable-line react-hooks/exhaustive-deps

  const sourceChartData = useMemo(() => {
    if (!data?.month_labels || !data?.by_source) return [];
    return data.month_labels.map((m, idx) => {
      const point = { month: formatMonth(m) };
      data.by_source.forEach(s => { point[s.source] = s.monthly[idx]?.count || 0; });
      return point;
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeClients = useMemo(() => data?.by_client?.filter(c => c.total > 0) || [], [data]);

  const chartEntries = view === 'client'
    ? (selectedClient === 'all' ? activeClients : data?.by_client?.filter(c => c.client_id === selectedClient) || [])
    : view === 'consultant'
      ? (selectedConsultant === 'all' ? data?.by_consultant || [] : data?.by_consultant?.filter(c => c.consultant_id === selectedConsultant) || [])
      : data?.by_source || [];

  const chartData = view === 'client' ? clientChartData : view === 'consultant' ? consultantChartData : sourceChartData;

  const hasMissingMonths = data?.missing_months?.length > 0;
  const hasData = data?.computed_months?.length > 0;

  // Memoize the visible client options (filter+stats lookup) to avoid recomputing on each render
  const visibleClientOptions = useMemo(
    () => clientsList
      .filter(c => c.include_in_stats !== false && c.status !== 'archived')
      .map(c => ({
        ...c,
        stats: data?.by_client?.find(b => b.client_id === c.id),
      })),
    [clientsList, data],
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="lead-analytics-page">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Analyse des leads</h1>
          <p className="text-neutral-500 mt-1">Statistiques mensuelles par client, consultant et source</p>
        </div>

        {(gmailStatus?.expired || gmailStatus?.connected === false) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="gmail-disconnected-banner">
            <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Link2Off className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-red-900">Gmail déconnecté</p>
              <p className="text-sm text-red-700 mt-0.5">
                Le token Gmail a expiré ou été révoqué. Reconnectez-vous pour pouvoir indexer les emails et calculer les leads.
              </p>
            </div>
            <Link to="/leads">
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid="reconnect-gmail-cta">
                Reconnecter Gmail
              </Button>
            </Link>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="flex gap-1 bg-neutral-100 rounded-lg p-1">
            {[{ id: 'client', label: 'Par client' }, { id: 'consultant', label: 'Par consultant' }, { id: 'source', label: 'Par source' }].map(tab => (
              <button key={tab.id}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === tab.id ? 'bg-white text-[#002FA7] shadow-sm' : 'text-neutral-600'}`}
                onClick={() => setView(tab.id)}
                data-testid={`view-${tab.id}-btn`}
              >{tab.label}</button>
            ))}
          </div>

          {view === 'client' && (
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-[220px]" data-testid="filter-client">
                <SelectValue placeholder="Tous les clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {visibleClientOptions.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`option-client-${c.id}`}>
                    {c.name}{c.stats ? ` (${c.stats.total})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {view === 'consultant' && (
            <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
              <SelectTrigger className="w-[220px]" data-testid="filter-consultant">
                <SelectValue placeholder="Tous les consultants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les consultants</SelectItem>
                {(data?.by_consultant || []).map(c => (
                  <SelectItem key={c.consultant_id} value={c.consultant_id}>{c.consultant_name} ({c.total})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex gap-2">
            {[3, 4, 5, 6].map(m => (
              <Button key={m} size="sm" variant={months === m ? 'default' : 'outline'}
                className={months === m ? 'bg-[#002FA7] hover:bg-[#002277]' : ''}
                onClick={() => handleMonthsChange(m)} data-testid={`months-${m}`}
              >{m} mois</Button>
            ))}
          </div>

          {hasMissingMonths && (
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={computeAll} disabled={computing} data-testid="compute-all-btn">
              {computing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Calculer {data.missing_months.length} mois manquant{data.missing_months.length > 1 ? 's' : ''}
            </Button>
          )}

          {hasData && (
            <Button size="sm" variant="ghost" className="text-neutral-500"
              onClick={() => fetchData(months)} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Rafraichir
            </Button>
          )}

          {user?.role === 'admin' && clientsList.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setShowFilterPanel(s => !s)}
                    data-testid="toggle-client-filter"
                    className={showFilterPanel ? 'bg-neutral-100' : ''}>
              <Users className="w-4 h-4 mr-1" /> Clients inclus ({clientsList.filter(c => c.include_in_stats !== false && c.status !== 'archived').length}/{clientsList.length})
            </Button>
          )}

          {user?.role === 'admin' && (
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={recomputeAll} disabled={recomputing || computing} data-testid="recompute-all-btn">
              {recomputing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {recomputing ? 'Recalcul en cours...' : 'Recalculer tout'}
            </Button>
          )}

          {user?.role === 'admin' && (
            <Button size="sm" variant="outline" className="border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
              onClick={triggerScheduler} disabled={triggeringScheduler} data-testid="trigger-scheduler-btn">
              {triggeringScheduler ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Calcul auto
            </Button>
          )}
        </div>

        {showFilterPanel && clientsList.length > 0 && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4" data-testid="client-filter-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-sm text-neutral-900">Clients inclus dans les totaux</h3>
                <p className="text-xs text-neutral-500">Décochez un client pour l'exclure des statistiques (leads conservés en base).</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="text-xs h-7"
                        onClick={() => clientsList.forEach(c => c.include_in_stats === false && toggleClientInStats(c.id, true))}
                        data-testid="include-all-btn">
                  Tout cocher
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {clientsList.map(c => {
                const included = c.include_in_stats !== false && c.status !== 'archived';
                return (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-2 py-1.5 border border-transparent hover:border-neutral-200"
                         data-testid={`filter-client-${c.id}`}>
                    <input type="checkbox" checked={included}
                           onChange={e => toggleClientInStats(c.id, e.target.checked)}
                           className="w-4 h-4 rounded accent-[#002FA7]" />
                    <span className={`truncate ${!included ? 'text-neutral-400 line-through' : 'text-neutral-800'}`}>
                      {c.name}
                    </span>
                    {c.status === 'archived' && (
                      <span className="ml-auto text-xs text-neutral-400">archivé</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {loading && (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#002FA7]" />
            <p className="text-neutral-500 text-sm mt-3">Chargement des statistiques...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {data && !loading && (
          <>
            {hasData && (
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="recompute-months-banner">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-neutral-800 text-sm font-medium">
                      Recalculer un mois spécifique
                    </p>
                    <p className="text-neutral-500 text-xs mt-1">
                      Clique sur un mois pour re-indexer les emails Gmail de cette période. Utile après réception de nouveaux leads.
                      {hasMissingMonths && <> Les mois en <span className="text-amber-700 font-medium">jaune</span> ne sont pas encore calculés.</>}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {data.month_labels.map(ym => {
                        const isMissing = data.missing_months?.includes(ym);
                        return (
                          <Button key={ym} size="sm" variant="outline"
                            className={`text-xs h-7 ${isMissing ? 'border-amber-300 hover:bg-amber-100 text-amber-700' : 'border-neutral-300 hover:bg-neutral-50'}`}
                            onClick={() => computeMonth(ym)}
                            disabled={computingMonth === ym || computing}
                            data-testid={`compute-month-${ym}`}>
                            {computingMonth === ym ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                            {formatMonth(ym)}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasMissingMonths && !hasData && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="missing-months-banner">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-800 text-sm font-medium">
                      {data.missing_months.length} mois non calcule{data.missing_months.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-amber-700 text-xs mt-1">
                      Cliquez sur un mois ou "Calculer tous" pour indexer les emails Gmail.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {data.missing_months.map(ym => (
                        <Button key={ym} size="sm" variant="outline"
                          className="text-xs h-7 border-amber-300 hover:bg-amber-100"
                          onClick={() => computeMonth(ym)}
                          disabled={computingMonth === ym || computing}
                          data-testid={`compute-month-init-${ym}`}>
                          {computingMonth === ym ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          {formatMonth(ym)}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard icon={Database} value={data.grand_total || data.total_matched} label="Total leads (societe)" color="text-[#002FA7]" />
              <SummaryCard icon={Users} value={data.total_matched} label="Attribues a un client" color="text-[#2A9D8F]" />
              <SummaryCard icon={TrendingUp} value={activeClients.length} label="Clients actifs" color="text-amber-600" />
              <SummaryCard icon={CheckCircle2} value={data.computed_months?.length || 0} label={`Mois calcules / ${data.month_labels?.length || 0}`} color="text-neutral-700" />
            </div>

            {companyChartData.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="company-chart">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold text-lg text-neutral-900">Total leads - Societe</h2>
                  <span className="text-2xl font-mono font-bold text-[#002FA7]">{data.grand_total || 0}</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={companyChartData}>
                    <defs>
                      <linearGradient id="companyGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#002FA7" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#002FA7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={xTick} />
                    <YAxis tick={yTick} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="Total Leads" stroke="#002FA7" strokeWidth={2.5} fill="url(#companyGrad)" dot={COMPANY_DOT} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {chartData.length > 0 && chartEntries.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="detail-chart">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
                  {view === 'client' ? 'Leads par client' : view === 'consultant' ? 'Leads par consultant' : 'Leads par source'} — {months} derniers mois
                </h2>
                <ResponsiveContainer width="100%" height={350}>
                  {view === 'source' ? (
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={xTick} />
                      <YAxis tick={yTick} allowDecimals={false} />
                      <Tooltip />
                      {chartEntries.map((entry) => (
                        <Area key={entry.source} type="monotone" dataKey={entry.source}
                          fill={getSourceColor(entry.source)} stroke={getSourceColor(entry.source)}
                          fillOpacity={0.6} stackId="1" />
                      ))}
                    </AreaChart>
                  ) : (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={xTick} />
                      <YAxis tick={yTick} allowDecimals={false} />
                      <Tooltip />
                      {chartEntries.map((entry, idx) => (
                        <Line key={entry.client_id || entry.consultant_id}
                          type="monotone" dataKey={view === 'client' ? entry.client_name : entry.consultant_name}
                          stroke={COLORS[idx % COLORS.length]} strokeWidth={2} dot={LINE_DOT} connectNulls />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
                <div className="flex gap-4 justify-center flex-wrap mt-3">
                  {chartEntries.map((entry, idx) => {
                    const name = view === 'source' ? entry.source : view === 'client' ? entry.client_name : entry.consultant_name;
                    const color = view === 'source' ? getSourceColor(entry.source) : COLORS[idx % COLORS.length];
                    return (
                      <div key={name} className="flex items-center gap-1.5 text-xs">
                        <div className="w-3 h-3 rounded-full" style={{ background: color }} />{name}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {view === 'source' && data.by_source?.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">Detail par source</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200 text-left text-neutral-500">
                        <th className="pb-2 pr-4">Source</th>
                        {data.month_labels.map(m => (
                          <th key={m} className="pb-2 px-2 text-right text-xs font-normal">{formatMonth(m)}</th>
                        ))}
                        <th className="pb-2 px-2 text-right font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_source.map(s => (
                        <tr key={s.source} className="border-b border-neutral-50 hover:bg-neutral-50">
                          <td className="py-2.5 pr-4 font-medium flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: getSourceColor(s.source) }} />
                            {s.source}
                          </td>
                          {s.monthly.map((m, idx) => (
                            <td key={data.month_labels[idx]} className="py-2.5 px-2 text-right font-mono">
                              {m.count > 0 ? <span className="font-medium">{m.count}</span> : <span className="text-neutral-300">0</span>}
                            </td>
                          ))}
                          <td className="py-2.5 px-2 text-right font-mono font-bold">{s.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {view === 'client' && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">Detail par client</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200 text-left text-neutral-500">
                        <th className="pb-2 pr-4">Client</th>
                        <th className="pb-2 pr-2">Consultant</th>
                        <th className="pb-2 pr-2">Sources</th>
                        {data.month_labels.map(m => (
                          <th key={m} className="pb-2 px-2 text-right text-xs font-normal">{formatMonth(m)}</th>
                        ))}
                        <th className="pb-2 px-2 text-right font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedClient === 'all' ? data.by_client : data.by_client.filter(c => c.client_id === selectedClient)).map(c => (
                        <tr key={c.client_id} className="border-b border-neutral-50 hover:bg-neutral-50">
                          <td className="py-2.5 pr-4 font-medium text-neutral-800">{c.client_name}</td>
                          <td className="py-2.5 pr-2 text-neutral-500 text-xs">{c.consultant_name || 'Non assigne'}</td>
                          <td className="py-2.5 pr-2">
                            <div className="flex gap-1 flex-wrap">
                              {c.sources?.slice(0, 3).map(s => (
                                <span key={s.source} className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-neutral-100">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: getSourceColor(s.source) }} />
                                  {s.count}
                                </span>
                              ))}
                            </div>
                          </td>
                          {c.monthly.map((m, idx) => (
                            <td key={data.month_labels[idx]} className="py-2.5 px-2 text-right font-mono">
                              {m.count > 0 ? <span className="text-[#002FA7] font-medium">{m.count}</span> : <span className="text-neutral-300">0</span>}
                            </td>
                          ))}
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-neutral-900">{c.total}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-neutral-300 font-bold">
                        <td className="py-2.5" colSpan={3}>TOTAL</td>
                        {data.month_labels.map((m, idx) => {
                          const total = data.by_client.reduce((s, c) => s + (c.monthly[idx]?.count || 0), 0);
                          return <td key={m} className="py-2.5 px-2 text-right font-mono">{total}</td>;
                        })}
                        <td className="py-2.5 px-2 text-right font-mono">{data.total_matched}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {view === 'consultant' && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">Detail par consultant</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200 text-left text-neutral-500">
                        <th className="pb-2 pr-4">Consultant</th>
                        <th className="pb-2 pr-4">Clients</th>
                        {data.month_labels.map(m => (
                          <th key={m} className="pb-2 px-2 text-right text-xs font-normal">{formatMonth(m)}</th>
                        ))}
                        <th className="pb-2 px-2 text-right font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedConsultant === 'all' ? data.by_consultant : data.by_consultant.filter(c => c.consultant_id === selectedConsultant)).map(c => (
                        <tr key={c.consultant_id} className="border-b border-neutral-50 hover:bg-neutral-50">
                          <td className="py-2.5 pr-4 font-medium text-neutral-800">{c.consultant_name}</td>
                          <td className="py-2.5 pr-4 text-neutral-500 text-xs">{c.clients_count} clients</td>
                          {c.monthly.map((m, idx) => (
                            <td key={data.month_labels[idx]} className="py-2.5 px-2 text-right font-mono">
                              {m.count > 0 ? <span className="text-[#002FA7] font-medium">{m.count}</span> : <span className="text-neutral-300">0</span>}
                            </td>
                          ))}
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-neutral-900">{c.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.total_unmatched > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-4" data-testid="unmatched-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-800 font-medium">{data.total_unmatched} emails non attribues a un client</p>
                    <p className="text-amber-700 text-xs mt-1">Consultez la liste pour identifier les termes a ajouter dans les mots-cles de detection.</p>
                  </div>
                  {user?.role === 'admin' && !debugData && (
                    <div className="flex gap-2">
                      {data.month_labels.map(m => (
                        <Button key={m} size="sm" variant="outline"
                          className="text-xs h-7 border-amber-300 hover:bg-amber-100"
                          onClick={() => loadUnmatchedEmails(m)}
                          disabled={debugLoading}
                          data-testid={`debug-month-${m}`}>
                          {debugLoading && debugMonth === m ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          {formatMonth(m)}
                        </Button>
                      ))}
                    </div>
                  )}
                  {debugData && (
                    <Button size="sm" variant="outline" onClick={() => setDebugData(null)} className="text-xs">
                      Fermer la liste
                    </Button>
                  )}
                </div>

                {debugData && (
                  <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
                    <div className="p-3 border-b border-amber-100 bg-amber-50/50">
                      <p className="text-sm font-medium text-amber-900">
                        {formatMonth(debugData.month)} — {debugData.unmatched_count} non attribues / {debugData.total_emails} total
                      </p>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b border-neutral-200 text-left text-neutral-500">
                            <th className="p-2">#</th>
                            <th className="p-2">Expediteur</th>
                            <th className="p-2">Sujet</th>
                            <th className="p-2">Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debugData.unmatched_samples.map((email, idx) => (
                            <tr key={`${email.from}-${idx}`} className="border-b border-neutral-50 hover:bg-amber-50/30">
                              <td className="p-2 text-neutral-400 font-mono">{idx + 1}</td>
                              <td className="p-2 text-neutral-700 max-w-[250px] truncate" title={email.from}>{email.from}</td>
                              <td className="p-2 text-neutral-600 max-w-[350px] truncate" title={email.subject}>{email.subject}</td>
                              <td className="p-2 text-neutral-500">{email.source || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="p-3 border-t border-amber-100 bg-amber-50/50 text-xs text-amber-700">
                      Conseil : Reperer les noms de clients dans les expediteurs/sujets, puis aller dans Clients → menu → "Mots-cles leads" pour ajouter ces termes. Ensuite cliquer "Recalculer tout".
                    </div>
                  </div>
                )}
              </div>
            )}

            {!hasData && !hasMissingMonths && (
              <div className="text-center py-12">
                <Database className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
                <p className="text-neutral-500">Aucune donnee disponible</p>
              </div>
            )}

            {user?.role === 'admin' && (
              <ROISection clients={clientsList} onSettingsUpdated={fetchClients} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function SummaryCard({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid={`summary-${label}`}>
      <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <p className={`font-mono text-2xl font-bold ${color}`}>{typeof value === 'number' ? value.toLocaleString('fr-FR') : value || 0}</p>
    </div>
  );
}
